import {
  DialogActions,
  DialogContent,
  MenuItem,
  Typography,
} from '@material-ui/core'
import CancelButton from 'app/components/buttons/CancelButton'
import {
  ButtonWithLoading,
  TextInput,
} from 'app/components/FormsyHelperComponent'
import { StyleSelectorFormsy } from 'app/main/notification/components/StyleSelectFormsy'
import clsx from 'clsx'
import { useFormsy } from 'commons/form'
import Formsy from 'formsy-react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useReducer, useRef, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import CheckboxTreeFormsy from './CheckboxTreeFormsy'
import { ADMIN, AUTHOR, RoleTypes } from './Constants'
import useRoleForm from './useRoleForm'
import PermissionTreeFormsy from './PermissionTreeFormsy'
import { isHomelessGroup, getGroupOptions } from 'commons/groupLogic'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import { makeStyles } from '@material-ui/styles'
import { useBreakpoints } from 'commons/useBreakpoints'
import { useAppName } from 'commons/usePartner'

const useStyles = makeStyles((theme) => ({
  longLabelTextField: {
    [useBreakpoints(theme).lessThan.sm]: {
      '& legend': {
        maxWidth: '75vw',
      },
    },
  },
}))

const parseRoleTypes = (value, index) => ({
  id: index,
  type: value,
})

const Label = {
  RoleType: {
    [ADMIN]: 'roleForm.type.admin',
    [AUTHOR]: 'roleForm.type.author',
  },
  SubmitButton: {
    [true]: 'roleForm.btn.create',
    [false]: 'saveBtn',
  },
  Title: {
    [true]: 'roleForm.create.label',
    [false]: 'roleForm.update.label',
  },
}

const actionTypes = {
  SUBMIT_INIT: 'SUBMIT_INIT',
  SUBMIT_SUCCESS: 'SUBMIT_SUCCESS',
  VALID_FORM: 'VALID_FORM',
  INVALID_FORM: 'INVALID_FORM',
}

const initialState = {
  isFormValid: false,
  isSubmitting: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SUBMIT_INIT: {
      return {
        ...state,
        isSubmitting: true,
      }
    }
    case actionTypes.SUBMIT_SUCCESS: {
      return {
        ...state,
        isSubmitting: false,
      }
    }
    case actionTypes.VALID_FORM: {
      return {
        ...state,
        isFormValid: true,
      }
    }
    case actionTypes.INVALID_FORM: {
      return {
        ...state,
        isFormValid: false,
      }
    }
    default: {
      throw new Error('Not supported action')
    }
  }
}

const getGroupIds = (initialData, groups) =>
  R.compose(
    R.uniq,
    R.filter(R.complement(R.isNil)),
    R.prepend(R.path(['tags', 0, 'id'], R.find(isHomelessGroup, groups))),
    R.map(R.prop('id')),
    R.propOr([], 'groups'),
  )(initialData)

function RoleFormDialog(props) {
  const { isOpen, onClose, onSubmit, initialData = {} } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const classes = useStyles()
  const formRef = useRef()
  const isAdding = R.isEmpty(initialData)
  const {
    state: { type: roleType },
    onChange: onChangeRoleForm,
    resetForm,
    data: [{ data: permissions }, { data: groups }],
    isLoading,
  } = useRoleForm({ formRef, isOpen, onError: onClose })
  const appName = useAppName()

  const serializePermissions = R.over(
    R.lensProp('permissionIds'),
    R.unless(R.isNil, R.compose(R.join(','), R.sort(R.subtract))),
  )
  const { isPristine, onChange } = useFormsy(
    formRef,
    (prisineValue, currentValue) =>
      R.equals(
        serializePermissions(prisineValue),
        serializePermissions(currentValue),
      ),
  )

  const handleSubmit = async (value) => {
    dispatch({ type: actionTypes.SUBMIT_INIT })
    await onSubmit(R.mergeAll([value, R.pick(['id'], initialData)]))
    dispatch({ type: actionTypes.SUBMIT_SUCCESS })
    resetForm()
  }

  const handleClose = R.compose(onClose, resetForm)

  const isNotAdmin =
    R.defaultTo(R.prop('type', initialData), roleType) !== ADMIN

  useEffect(() => {
    if (isOpen) {
      // reset isPristine every time opening dialog
      onChange()
    }
  }, [isOpen, onChange])

  if (!isOpen || isLoading) return null

  return (
    <ResponsiveDialog
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
      fullWidth
      maxWidth="xs"
      aria-labelledby="scroll-dialog-title"
      classes={{ paper: 'overflow-y-visible' }}
    >
      <Formsy
        onChange={R.compose(onChangeRoleForm, onChange)}
        onValidSubmit={handleSubmit}
        onValid={() => dispatch({ type: actionTypes.VALID_FORM })}
        onInvalid={() => dispatch({ type: actionTypes.INVALID_FORM })}
        className="flex flex-col"
        ref={formRef}
      >
        <DialogContent classes={{ root: 'p-24' }}>
          <Typography variant="h6" className={clsx('mb-8')}>
            <FormattedMessage id={Label.Title[isAdding]} />
          </Typography>

          {isAdding && (
            <React.Fragment>
              <div className="mb-8">
                <Typography variant="caption">
                  <FormattedMessage
                    id="roleForm.create.subtitle1"
                    values={{ appName }}
                  />
                </Typography>
              </div>
              <div className="mb-8">
                <Typography variant="caption">
                  <FormattedMessage id="roleForm.create.subtitle2" />
                </Typography>
              </div>
            </React.Fragment>
          )}

          <div className="flex-1 mb-24">
            <StyleSelectorFormsy
              label={formatMessage({ id: 'roleForm.type.label' })}
              name="type"
              value={R.prop('type', initialData)}
              FormControlProps={{
                required: true,
              }}
              required
            >
              {RoleTypes.map(parseRoleTypes).map((item) => (
                <MenuItem key={item.id} value={item.type}>
                  <FormattedMessage id={Label.RoleType[item.type]} />
                </MenuItem>
              ))}
            </StyleSelectorFormsy>
          </div>

          <div className={clsx('flex-1', { 'mb-24': isNotAdmin })}>
            <TextInput
              labelId="roleForm.name.label"
              placeholderId="roleForm.name.placeHolder"
              name="name"
              value={R.prop('name', initialData)}
              required
              maxLength={40}
              fullWidth
            />
          </div>

          {isNotAdmin && (
            <React.Fragment>
              <div className="flex mb-24">
                <CheckboxTreeFormsy
                  label={formatMessage({ id: 'roleForm.groups.label' })}
                  name="groupIds"
                  variant="outlined"
                  className={clsx(classes.longLabelTextField, 'w-full')}
                  emptyLabelId="roleForm.groups.empty"
                  value={getGroupIds(initialData, groups)}
                  multiple
                  options={getGroupOptions(formatMessage, groups)}
                />
              </div>

              <div className="flex">
                <PermissionTreeFormsy
                  label={formatMessage({ id: 'roleForm.permissions.label' })}
                  name="permissionIds"
                  variant="outlined"
                  className={clsx(classes.longLabelTextField, 'w-full')}
                  value={R.propOr([], 'permissions', initialData)}
                  permissions={permissions}
                />
              </div>
            </React.Fragment>
          )}
        </DialogContent>
        <DialogActions className="justify-end ltr:pl-16 rtl:pr-16">
          <CancelButton onClick={handleClose} />
          <ButtonWithLoading
            isLoading={state.isSubmitting}
            labelId={Label.SubmitButton[isAdding]}
            disabled={!state.isFormValid || state.isSubmitting || isPristine}
            type="submit"
          />
        </DialogActions>
      </Formsy>
    </ResponsiveDialog>
  )
}

RoleFormDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    avatar: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    isSuspended: PropTypes.bool,
  }),
}

export default RoleFormDialog
